import { useFeature } from "@hackler/react-sdk";

/**
 * 2026 추석 연휴(9/23 21:00 ~ 9/28 09:00) 콜 차단 스위치.
 *
 * 연휴에는 상담 인력이 최소로 돌아 전화를 받지 못한다. 못 받을 전화를 유도하면
 * 급한 상황에 건 사람에게 제일 나쁜 경험이 되므로, 콜 표면만 내리고 폼(견적,
 * 체크리스트, 홈질문)은 그대로 둔다. 폼은 연휴가 끝나고 처리해도 되기 때문이다.
 *
 * 켜고 끄는 방법이 두 가지다.
 * 1. 핵클 기능 플래그. 9/23 21:00 켜고 9/28 09:00 끄도록 예약을 건다.
 * 2. 핵클이 안 먹을 때를 위한 수동 스위치. FORCE 를 true 로 바꾸고 배포하면 된다.
 *
 * 둘 중 하나만 켜져도 차단이 걸린다(OR). 기본값은 둘 다 꺼짐이라 배포해도 화면이 변하지 않는다.
 */

/**
 * 핵클 기능 플래그 키. ON 이면 차단이다.
 *
 * 앱마다 Hackle SDK 키(=워크스페이스)가 달라 플래그 번호도 갈린다.
 * - platform, ssr-platform, parlor : 30
 * - 독사운드 6개 포털 : 4 (dogsound 레포 goi_common 에 따로 있다)
 *
 * 작년(2025)에는 `useOffDirectCall` 이 쓰는 4번을 재사용했다. 그 플래그는 평상시
 * 심야, 주말 콜 차단을 물고 있고 극성도 반대(OFF 가 차단)라 섞으면 안 된다.
 *
 * null 로 두면 핵클을 아예 보지 않고 FORCE 만 본다.
 */
const CHUSEOK_2026_FEATURE_KEY: number | null = 30;

/** 핵클 없이 강제로 켜는 스위치. 연휴에 핵클이 안 먹으면 이 줄만 true 로 바꿔 배포한다. */
const CHUSEOK_2026_FORCE = false;

export const useChuseokOff = (): boolean => {
  /** 훅은 조건부 호출이 안 되므로 키가 없을 때도 호출은 하되 결과를 무시한다. */
  const featureOn = useFeature(CHUSEOK_2026_FEATURE_KEY ?? 0);

  if (CHUSEOK_2026_FORCE) return true;
  if (CHUSEOK_2026_FEATURE_KEY === null) return false;

  return featureOn;
};

/**
 * 콜이 발생하면 안 되는 시설 페이지인지.
 *
 * 개별 상세와 지역 목록을 모두 잡아야 해서 부분일치로 둔다.
 * - `/facilities/` : 개별 장지, 지역 장지, 해수장, 시설탐색
 * - `/crematorium` : 개별 화장장, 지역 화장장, crematorium-recommendation
 * - `/hospice/`, `/care/` : 호스피스, 요양원 (ssr-platform)
 * - `/information-price/facility/` : 장지 가격 정보
 * - `/facility-relocate/` : 개장이장
 *
 * 뒤의 둘은 앞 네 조건에 안 걸려서 따로 적는다. `facility` 하나로 줄이면
 * `/match/facility-requests/` 같은 견적 퍼널까지 걸리므로 줄이지 않는다.
 *
 * 헤더를 통째로 숨기는 기준이기도 하다. 개별 메뉴를 하나씩 끄면 갑기님이 붙이는
 * 시설탐색이 헤더에 올라올 때 또 빠지므로, 경로 기준으로 헤더 전체를 내린다.
 */
export const isChuseokOffPath = (asPath: string): boolean =>
  asPath.includes("/facilities/") ||
  asPath.includes("/crematorium") ||
  asPath.includes("/hospice/") ||
  asPath.includes("/care/") ||
  asPath.includes("/information-price/facility/") ||
  asPath.includes("/facility-relocate/");
